/**
 * Thin client for the Python/Ollama chat-backend.
 *
 * The backend lives at HFCL_BACKEND_URL and exposes:
 *   POST /api/chat/classify
 *     body = {
 *       text,                    // current user message (required)
 *       page_context?,           // { route, section, ... }
 *       session_id?,             // stable id per browser session
 *       intent?,                 // pre-resolved intent id — skips classification
 *       history?: [              // last N turns, oldest first
 *         { role: 'user' | 'assistant', text: '...' },
 *         ...
 *       ]
 *     }
 *
 * It returns either:
 *   { type: "action",  intent, confidence, args, reply, source? }
 *   { type: "message", confidence, reply, suggestions? }
 *
 * If anything goes wrong (network error, timeout, non-2xx, malformed
 * response) the caller catches and falls back to the local matcher.
 */

import {
	HFCL_USE_AGENT,
	HFCL_BACKEND_URL,
	HFCL_BACKEND_TIMEOUT_MS,
} from './hfclChatConfig';
import {
	dismissActiveAgentModal,
	isCapabilitySupported,
	sendAgentMessage,
} from '../../agentBridge';

/**
 * Send a user message to the backend classifier.
 *
 * @param {string} text - user message
 * @param {Object} [opts]
 * @param {Object} [opts.pageContext] - { route, ...whatever the page wants to share }
 * @param {string} [opts.sessionId]   - stable per-browser-session id
 * @param {Array}  [opts.history]     - last N turns: [{ role, text }, ...]
 * @param {string} [opts.intent]      - pre-resolved intent id (e.g. from a
 *                                      suggestion chip). When set, the
 *                                      backend skips classification entirely
 *                                      and starts that intent's flow/hint
 *                                      directly instead of re-guessing from
 *                                      `text`.
 * @return {Promise<Object|null>} the parsed backend response, or null on error
 */
export async function classifyWithBackend(text, opts = {}) {
	if (HFCL_USE_AGENT) {
		try {
			const selectedCapabilityId = opts.intent &&
				isCapabilitySupported(opts.intent)
				? opts.intent
				: null;
			const response = await sendAgentMessage(text, {
				sessionId: opts.sessionId,
				pageContext: opts.pageContext,
				selectedCapabilityId,
				selectedChoiceId: opts.intent && !selectedCapabilityId
					? opts.intent
					: null,
			});
			if (response.workflow?.state === 'cancelled') {
				dismissActiveAgentModal();
			}
			window.dispatchEvent(new CustomEvent('canvas-agent:response', {
				detail: {response, sessionId: opts.sessionId},
			}));
			return {
				type: 'message',
				confidence: 1,
				reply: response.message,
				suggestions: response.suggestions?.map((item) => ({
					label: item.label,
					next_intent: item.id,
				})),
			};
		} catch (err) {
			console.warn('[canvas-agent] request failed:', err?.message);
			return {
				type: 'message',
				confidence: 1,
				reply:
					'The SSID agent could not process this request. ' +
					'Check the /api/agent/chat response in Network.',
				suggestions: null,
			};
		}
	}
	const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
	const timer = ctrl
		? setTimeout(() => ctrl.abort(), HFCL_BACKEND_TIMEOUT_MS)
		: null;

	try {
		const res = await fetch(`${HFCL_BACKEND_URL}/api/chat/classify`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				text,
				page_context: opts.pageContext || null,
				session_id: opts.sessionId || null,
				intent: opts.intent || null,
				// Trim each turn to keep the payload small; the LLM has a
				// finite context window and we want to leave room for the
				// intent registry + reasoning. 400 chars per turn is plenty
				// for navigation queries.
				history: Array.isArray(opts.history)
					? opts.history.map((t) => ({
						role: t.role === 'assistant' ? 'assistant' : 'user',
						text: String(t.text || '').slice(0, 400),
					}))
					: [],
			}),
			signal: ctrl ? ctrl.signal : undefined,
		});
		if (!res.ok) {
			console.warn('[hfcl-chat] backend returned', res.status);
			return null;
		}
		return await res.json();
	} catch (err) {
		console.warn('[hfcl-chat] backend call failed:', err && err.message);
		return null;
	} finally {
		if (timer) clearTimeout(timer);
	}
}

/**
 * Fire-and-forget flow event notification.
 *
 * The frontend calls this when a step in an active flow completes — for
 * example, when the AddFirmware modal saves successfully:
 *
 *   emitChatEvent({
 *     sessionId: 'chat-lyz3k2-a8d92xqr',
 *     event:     'firmware-uploaded',
 *     data:      { firmware_name: '13.3.1.4' },
 *   });
 *
 * The backend advances the current flow and returns the next node's
 * reply / actions / suggestions. We POST but don't block on the
 * response (204 when there's no active flow); the useHfclChat hook
 * subscribes separately via a lightweight polling / EventSource
 * mechanism if we ever need push. For the MVP the response body is
 * returned so the caller can push it into the chat.
 *
 * Returns the parsed response (with `reply`, `actions`, `suggestions`,
 * `flow_state`) OR null if there was no active flow OR the request
 * failed. Never throws.
 */
export async function emitChatEvent({ sessionId, event, data = {} } = {}) {
	if (!sessionId || !event) return null;
	try {
		const res = await fetch(`${HFCL_BACKEND_URL}/api/chat/event`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ session_id: sessionId, event, data }),
		});
		// 204 = no-op (no active flow waiting on this event)
		if (res.status === 204) return null;
		if (!res.ok) {
			console.warn('[hfcl-chat] /event returned', res.status);
			return null;
		}
		return await res.json();
	} catch (err) {
		console.warn('[hfcl-chat] /event call failed:', err && err.message);
		return null;
	}
}
